import { customElement, property, state } from 'lit/decorators.js';
import { CSSResult, html, LitElement, nothing, PropertyValues, unsafeCSS } from 'lit';
import { eventApi } from '../../../scripts/product-events/event-api.js';
import '../event-web-layer/event-web-layer.js';
import { WishlistAppConfig, wishlistAppConfigContext } from '../../../scripts/context/appConfigContext.js';
import { Events, Messages } from '../../../@types/wishlist.js';
import { consume } from '@lit/context';
import clockAlarm from '@breuni-ui/design-tokens/images/icons/o-ic-20_clock-alarm.svg?element';
import styles from './event-icon.scss?inline';
import { sharedCss } from '../../../scripts/style-bundle/shared.js';
import { i18nContext } from '../../../scripts/context/i18nContext.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { trackUpdateIconClick, trackUpdateIconView } from '../../../scripts/service/cube-tracking-layer.js';
import { hideIconFromAssistiveTechnology } from '../icon/icon-accessibility.js';

@customElement('ents-event-icon')
export class EventIcon extends LitElement {
  @consume({ context: i18nContext })
  private messages!: Messages;

  @consume({ context: wishlistAppConfigContext })
  private wishlistAppConfig!: WishlistAppConfig;

  @property({ type: Boolean, attribute: 'logged-in' })
  private loggedIn: boolean = false;

  @state()
  private events: Events = { unseenProductEvents: [], seenProductEvents: [] };

  @state()
  private showLayer: boolean = false;

  @state()
  private blockNativeBottomSheetTrigger: boolean = false;

  private timeoutId?: ReturnType<typeof setTimeout>;

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles)];
  }

  connectedCallback() {
    super.connectedCallback();
    this.resetBlockState();
    this.loadEvents().catch((err) => console.error('Failed to load events:', err));
    // Just for cleanup of old values in localStorage.
    window.localStorage.removeItem('ents-wishlist-event-bell-tooltip');
    // re-enable button after native dialog has opened
    document.addEventListener('ents:wishlist-native-dialog-opened', this.resetBlockState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('ents:wishlist-native-dialog-opened', this.resetBlockState);
    this.resetBlockState();
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);

    const autoOpenViaUrl = new URL(window.location.href).searchParams.get('eventLayerOpen') === 'true';

    if (!autoOpenViaUrl) return;

    this.cleanupUrl();

    // Disabled until native bottom sheet is working properly
    // if (this.wishlistAppConfig.isAppNativeBottomSheetEnabled) {
    //   this.openNativeBottomSheet();
    // } else {
    this.openLayer();
    // }
  }

  private cleanupUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('eventLayerOpen');
    window.history.replaceState({}, '', url.toString());
  }

  async loadEvents() {
    if (!this.loggedIn) {
      return;
    }
    this.events = await eventApi.getEvents(this.wishlistAppConfig.eventsEndpoint);
    this.injectInterceptionObserver();
  }

  private openLayer = () => {
    this.showLayer = true;
  };

  private closeLayer() {
    this.showLayer = false;
    this.loadEvents().catch((err) => console.error('Failed to reload events:', err));
  }

  /* Disabled until native bottom sheet is working properly
  // this function includes a disabling mechanism for the button to prevent rage clicks triggering multiple
  // native bottom sheets, because the native bottom sheet may take a while to open
  private openNativeBottomSheet(attemptsOrEvent: number | Event = 0) {
    const attempts = typeof attemptsOrEvent === 'number' ? attemptsOrEvent : 0;

    if (attempts === 0) {
      if (this.blockNativeBottomSheetTrigger) return;

      this.blockNativeBottomSheetTrigger = true;

      // re-enable button after 2s as a fallback
      if (this.timeoutId) clearTimeout(this.timeoutId);
      this.timeoutId = setTimeout(() => {
        this.blockNativeBottomSheetTrigger = false;
        this.timeoutId = undefined;
      }, 2000);
    }

    try {
      openNativeDialog({
        'url': this.wishlistAppConfig.eventsAppLayerEndpoint,
        'height': 0.8,
        'draggable': false,
        'dismissible': true,
        'externalUrlPaths': ['/kauf/login/konto'],
      });
    } catch (error) {
      console.error('Error opening native dialog', error);
      if (attempts < 10) {
        setTimeout(() => this.openNativeBottomSheet(attempts + 1), 200);
      } else {
        this.resetBlockState(); // re-enable button after max retries
      }
    }
  } */

  private resetBlockState = () => {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }
    this.blockNativeBottomSheetTrigger = false;
  };

  private renderIcon(openLayer: () => void) {
    const unseenCount = this.events.unseenProductEvents.length;
    return html`
      <button
        class="ents-wishlist-event-icon"
        @click="${() => {
          this.trackIconClick();
          openLayer();
        }}"
        ?disabled="${this.blockNativeBottomSheetTrigger}"
        data-test-ents-event-icon
      >
        ${hideIconFromAssistiveTechnology(clockAlarm())}
        <div class="ents-wishlist-event-icon__name">${unsafeHTML(this.messages['ents_wishlist_event_icon_name'])}</div>
        ${
          unseenCount > 0
            ? html`<div class="bdk-badge-count" variant="coal" size="medium">${unseenCount}</div>`
            : nothing
        }
      </button>
    `;
  }

  render() {
    // Disabled until native bottom sheet is working properly
    // if (this.wishlistAppConfig.isAppNativeBottomSheetEnabled) {
    //   return html`
    //     ${this.renderIcon(this.openNativeBottomSheet)}
    //   `;
    // } else {
    return html`
      ${this.renderIcon(this.openLayer)}
      <ents-event-web-layer
        ?logged-in="${this.loggedIn}"
        .events="${this.events}"
        ?show-layer="${this.showLayer}"
        @ents-event-layer-close="${this.closeLayer}"
      ></ents-event-web-layer>
    `;
    //}
  }

  private areUpdatesAvailable() {
    return this.events.unseenProductEvents.length > 0;
  }

  private trackIconClick() {
    trackUpdateIconClick(this.areUpdatesAvailable());
  }

  private injectInterceptionObserver() {
    const options: IntersectionObserverInit = {
      threshold: 0.5,
    };

    const callback: IntersectionObserverCallback = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          trackUpdateIconView(this.areUpdatesAvailable());
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(callback, options);
    observer.observe(this);
  }
}
